/* eslint-disable react/jsx-max-props-per-line */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Grid, DialogContentText } from '@mui/material';
import moment from 'moment';
import {
  DialogDefaultV2,
  InputV2,
  InputFormatV2,
  OptionSelectV2,
  ArquivoContainerV2,
  WidgetV2,
} from '../../../../components/v2';

import * as selectorsAuth from '../../../../store/auth/reducer';
import * as selectorsNsir from '../../../../store/nsir/reducer';
import * as selectorsNsmp from '../../../../store/nsmp/reducer';
import * as actionsSubNsmp from '../../../../store/subNsmp/actions';
import * as actionsNsmp from '../../../../store/nsmp/actions';
import * as Role from '../../../../constants/role';
import * as Status from '../../../../constants/status';
import { isUsuarioUsoMuto, isUUID, isCessionariaOutros } from 'utils';

const rootGridSx = { pt: 1 };
const fieldContainerSx = { pb: 0 };
const posteContainerSx = {
  p: 1.5,
  border: 1,
  borderColor: 'divider',
  borderRadius: 1,
};
const requiredTextSx = { color: 'error.main', pt: 0.5 };
const evidenciaContentSx = { p: 2 };

const padCodigo = codigo => {
  const [first, ...rest] = codigo.split('_');
  const firstPadded = first.padStart(3, '0');
  return [firstPadded, ...rest].join('_');
};

const OcorrenciaRow = React.memo(function OcorrenciaRow({
  subNsmp,
  onUpdateSubNsmp,
  errors,
  canEditNsmp,
  enableInspecaoFields,
  enableDigitacaoFields,
  isRequiredResponsavel,
  digitadorAvailable,
  setOcorrenciaData,
  setClonarData,
  setDeletarData,
}) {
  const subNsmpCodigo = padCodigo(subNsmp.codigo);

  const actions = useMemo(() => {
    if (!digitadorAvailable && !canEditNsmp) return [];

    const invalidarOption = { onClick: () => setOcorrenciaData({ open: true, type: 'INVALIDADO', subNmspId: subNsmp.id, subNsmpCodigo }), title: 'Invalidar' };
    const validarOption = { onClick: () => setOcorrenciaData({ open: true, type: 'VALIDADO', subNmspId: subNsmp.id, subNsmpCodigo }), title: 'Validar' };
    const clonarOption = { onClick: () => setClonarData({ open: true, subNmspId: subNsmp.id, subNsmpCodigo }), title: 'Clonar' };
    const deletarOption = { onClick: () => setDeletarData({ open: true, subNmspId: subNsmp.id, subNsmpCodigo }), title: 'Deletar' };

    const availableOptions = [clonarOption];
    switch (subNsmp.validate) {
      case 'INVALIDADO':
        availableOptions.push(validarOption);
        break;
      case 'VALIDADO':
        availableOptions.push(invalidarOption);
        break;
      default:
        availableOptions.push(invalidarOption, validarOption);
        break;
    }
    if (!!subNsmp.idSubNsmpReference) availableOptions.push(deletarOption);
    return availableOptions;
  }, [subNsmp.validate, subNsmp.idSubNsmpReference, subNsmp.id, subNsmpCodigo, digitadorAvailable, canEditNsmp, setOcorrenciaData, setClonarData, setDeletarData]);

  let sufix = 'Aguardando Validação';
  if (subNsmp.validate === 'INVALIDADO') sufix = 'Inválido';
  else if (subNsmp.validate === 'VALIDADO') sufix = 'Valido';

  const isCessrionariaSelect = () => !subNsmp.responsavel || isUUID(subNsmp.responsavel);
  const showNomeCessionaria = () => isCessionariaOutros(subNsmp.responsavel);

  return (
    <Grid sx={evidenciaContentSx}>
      <WidgetV2 title={`Ocorrência ${subNsmpCodigo} - ${sufix}`} collapse actions={actions}>
        <Grid container spacing={1} sx={posteContainerSx}>
          <Grid size={{ xs: showNomeCessionaria() ? 3 : 4 }}>
            <OptionSelectV2
              label="Ocorrência de Irregularidade Técnica"
              codigo="SUBNSMP_USO_MUTUO_TIPO_SERVICO"
              isRequired
              onSelect={tipoServico => onUpdateSubNsmp(subNsmp.id, 'tipoServico', tipoServico)}
              value={subNsmp.tipoServico}
              error={errors && errors.tipoServico}
              disabled={canEditNsmp ? false : !enableInspecaoFields}
            />
          </Grid>
          <Grid size={{ xs: showNomeCessionaria() ? 3 : 4 }}>
            <OptionSelectV2
              label="Atuação"
              codigo="SUBNSMP_USO_MUTUO_ACAO"
              isRequired
              onSelect={acao => onUpdateSubNsmp(subNsmp.id, 'acao', acao)}
              value={subNsmp.acao}
              error={errors && errors.acao}
              disabled={canEditNsmp ? false : !enableInspecaoFields}
            />
          </Grid>
          <Grid size={{ xs: showNomeCessionaria() ? 3 : 4 }}>
            {!isCessrionariaSelect() && (
              <InputV2
                error={errors && errors.responsavel ? !isCessrionariaSelect() : false}
                isRequired={!isCessrionariaSelect() && isRequiredResponsavel}
                name="responsavel"
                label="Céssionária Responsável"
                value={subNsmp.responsavel}
                onChange={event => onUpdateSubNsmp(subNsmp.id, event.target.name, event.target.value)}
                disabled={canEditNsmp ? false : !enableInspecaoFields}
              />
            )}
            {isCessrionariaSelect() && (
              <OptionSelectV2
                label="Céssionária Responsável"
                codigo="SUBNSMP_USO_MUTUO_CESSIONARIA"
                isRequired={isCessrionariaSelect() && isRequiredResponsavel}
                onSelect={responsavel => onUpdateSubNsmp(subNsmp.id, 'responsavel', responsavel)}
                value={subNsmp.responsavel}
                error={errors && errors.responsavel ? isCessrionariaSelect() : false}
                disabled={canEditNsmp ? false : !enableInspecaoFields}
              />
            )}
          </Grid>
          {showNomeCessionaria() && (
            <Grid size={{ xs: 3 }}>
              <InputV2
                isRequired={isRequiredResponsavel}
                error={errors && errors.nomeCessionaria}
                name="nomeCessionaria"
                label="Nome Cessionária"
                value={subNsmp.nomeCessionaria}
                onChange={event => onUpdateSubNsmp(subNsmp.id, event.target.name, event.target.value)}
                disabled={canEditNsmp ? false : !enableInspecaoFields}
              />
            </Grid>
          )}
          <Grid size={{ xs: 12 }}>
            <InputV2
              label="Observação do Inspetor (outras informações)"
              name="observacaoInspetor"
              onChange={event => onUpdateSubNsmp(subNsmp.id, event.target.name, event.target.value)}
              multiline
              rows={2}
              value={subNsmp.observacaoInspetor}
              disabled={canEditNsmp ? false : !enableInspecaoFields}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <InputV2
              label="Observação do Digitador"
              name="observacaoDigitador"
              onChange={event => onUpdateSubNsmp(subNsmp.id, event.target.name, event.target.value)}
              multiline
              rows={2}
              value={subNsmp.observacaoDigitador}
              disabled={canEditNsmp ? false : !enableDigitacaoFields}
            />
          </Grid>
          <ArquivoContainerV2
            collapse
            arquivos={subNsmp.arquivos}
            disabled
            showDescription
          />
        </Grid>
      </WidgetV2>
    </Grid>
  );
});

const UsoMutuoNewModal = ({
  open,
  onCancel,
  onConfirm,
  selectedId,
  nsmps,
  nsmpSelected,
  loadNsmpById,
  readOnly,
  userRole,
  authData,
  nsirStatus,
  nsirTitle,
  digitadorId,
  inspetorId,
  userId,
  validateInvalidateSubNsmp,
  cloneSubNsmp,
  deleteCloneSubNsmp,
  onNext,
  onPrevious,
  hasNext,
  hasPrevious,
  carouselPosition,
}) => {
  const [canEditNsmp, setCanEditNsmp] = useState(false);
  const [readOnlyLocal, setReadOnlyLocal] = useState(readOnly);
  const [editFiles, setEditFiles] = useState(false);
  const [errors, setErrors] = useState({});
  const [values, setValues] = useState({ dataFotoDrone: moment().valueOf(), fotos: [] });
  const [ocorrenciaData, setOcorrenciaData] = useState({ open: false, type: '', subNmspId: null, subNsmpCodigo: null });
  const [clonarData, setClonarData] = useState({ open: false, subNmspId: null, subNsmpCodigo: null });
  const [deletarData, setDeletarData] = useState({ open: false, subNmspId: null, subNsmpCodigo: null });

  useEffect(() => {
    setReadOnlyLocal(readOnly);
  }, [readOnly]);

  useEffect(() => {
    if (selectedId) loadNsmpById(selectedId);
  }, [selectedId, loadNsmpById]);

  useEffect(() => {
    if (nsmpSelected && nsmpSelected.id) {
      setValues(nsmpSelected);
    }
  }, [nsmpSelected]);

  const handleChange = (id, value) => {
    setValues({
      ...values,
      [id]: value,
    });
  };

  const onUpdateSubNsmp = useCallback((subNsmpId, field, value) => {
    setValues(prev => ({
      ...prev,
      subNsmps: (prev.subNsmps || []).map(data =>
        data.id === subNsmpId ? { ...data, [field]: value } : data
      ),
    }));
  }, []);

  const clearForm = () => {
    setCanEditNsmp(false);
    setEditFiles(false);
    setErrors({});
    setValues({ fotos: [], dataFotoDrone: moment().valueOf() });
  };

  const onCancelLocal = () => {
    if (canEditNsmp) {
      setCanEditNsmp(false);
      setEditFiles(false);
      setErrors({});
      setReadOnlyLocal(true);
      if (nsmpSelected && nsmpSelected.id) {
        setValues(nsmpSelected);
      }
      return;
    }
    clearForm();
    onCancel();
  };

  const getDataFotoDrone = () => {
    let data = moment().valueOf();

    if (values.dataFotoDrone) {
      data = values.dataFotoDrone;
    }

    return moment(data).valueOf();
  };

  const buildPayload = () => {
    const data = {
      ...values,
      subNsmps: values.subNsmps ? values.subNsmps.map(sub => ({
        ...sub,
        arquivos: sub.arquivos.map(arq => {
          const { src, ...rest } = arq;
          return rest;
        }),
      })) : [],
    };
    return { ...data, codigo: getCodigo(), dataFotoDrone: getDataFotoDrone(), updateFiles: editFiles };
  };

  const onConfirmLocal = goNext => {
    setErrors({});
    onConfirm(buildPayload(), goNext);
  };

  const getCodigo = () => {
    if (values && values.id) return values.codigo;

    let codigo = Array.isArray(nsmps) && nsmps.length > 0
      ? nsmps.filter(n => n.fotoInicio !== true && n.fotoFim !== true && n.analiseRisco !== true).length + 1
      : 1;
    while ((codigo + '').length < 3) codigo = `0${codigo}`;
    return codigo;
  };

  const isAdmin = () => userRole === Role.ADMIN || userRole === Role.SUPER_ADMIN;

  const getTitle = () => {
    if (values && values.analiseRisco) {
      return `${nsirTitle} > Análise de Risco`;
    }

    return `${nsirTitle} > Poste ID ${values.codigo}`;
  };

  const renderOcorrencias = () => {
    const enableDigitacaoFields = nsirStatus === Status.EM_DIGITACAO && digitadorId === userId;
    const digitadorAvailable = enableDigitacaoFields;
    const isRequiredResponsavel = isAdmin() ? false : nsirStatus === Status.EM_DIGITACAO;

    return (
      <WidgetV2 title="Ocorrências">
        {(values.subNsmps || []).map(currentSubNsmp => {
          let enableInspecaoFields = nsirStatus === Status.EM_INSPECAO && inspetorId === userId;
          if (enableDigitacaoFields) {
            enableInspecaoFields = !!currentSubNsmp.idSubNsmpReference;
          }

          return (
            <OcorrenciaRow
              key={currentSubNsmp.id}
              subNsmp={currentSubNsmp}
              onUpdateSubNsmp={onUpdateSubNsmp}
              errors={errors}
              canEditNsmp={canEditNsmp}
              enableInspecaoFields={enableInspecaoFields}
              enableDigitacaoFields={enableDigitacaoFields}
              isRequiredResponsavel={isRequiredResponsavel}
              digitadorAvailable={digitadorAvailable}
              setOcorrenciaData={setOcorrenciaData}
              setClonarData={setClonarData}
              setDeletarData={setDeletarData}
            />
          );
        })}
      </WidgetV2>
    );
  };

  const sortByCode = subNsmpList =>
    [...subNsmpList].sort((a, b) => {
      const aParts = a.codigo.split('_').map(Number);
      const bParts = b.codigo.split('_').map(Number);
      const len = Math.max(aParts.length, bParts.length);

      for (let i = 0; i < len; i += 1) {
        const aVal = aParts[i] ?? 0;
        const bVal = bParts[i] ?? 0;

        if (aVal !== bVal) {
          return aVal - bVal;
        }
      }

      return aParts.length - bParts.length;
    });

  const isReadOnly = canEditNsmp ? false : nsirStatus === Status.EM_DIGITACAO ? false : readOnlyLocal;

  const buildHeaderActions = () => {
    const actions = [];
    if (carouselPosition) {
      actions.push({ title: '◄', onClick: onPrevious, disabled: !hasPrevious });
      actions.push({ title: `${carouselPosition.current} / ${carouselPosition.total}`, onClick: () => {}, disabled: true });
      actions.push({ title: '►', onClick: onNext, disabled: !hasNext });
    }
    if (!isUsuarioUsoMuto(authData.email) && isAdmin() && readOnlyLocal) {
      actions.push({ onClick: () => { setReadOnlyLocal(false); setCanEditNsmp(true); }, title: 'Editar' });
    }
    return actions.length > 0 ? actions : null;
  };

  const extraActions = hasNext && !isReadOnly ? [{ label: 'Salvar e Avançar', onClick: () => onConfirmLocal(true) }] : null;

  return (
    <>
      <DialogDefaultV2
        open={open}
        handlerCancel={onCancelLocal}
        handlerConfirm={() => onConfirmLocal(false)}
        title={getTitle()}
        confirmMessage="Salvar"
        fullScreen
        readOnly={isReadOnly}
        headerAction={buildHeaderActions()}
        extraActions={extraActions}
      >
        <Grid container spacing={1} sx={rootGridSx}>
          <Grid size={{ xs: 6, sm: 2 }}>
            <InputV2 name="numeroPoste" label="Identificador Poste" disabled value={getCodigo()} />
          </Grid>
          <Grid size={{ xs: 6, sm: 6 }}>
            <InputV2
              error={!!(errors && errors.endereco)}
              isRequired
              label="Endereço"
              name="endereco"
              onChange={event => handleChange(event.target.name, event.target.value)}
              value={values.endereco}
              disabled={readOnlyLocal}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <InputFormatV2
              error={!!(errors && errors.latitude)}
              name="latitude"
              label="Latitude"
              value={values.latitude}
              onChange={event => handleChange(event.target.name, event.target.value)}
              disabled={readOnlyLocal}
              format="-##,#######"
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <InputFormatV2
              error={!!(errors && errors.longitude)}
              name="longitude"
              label="Longitude"
              value={values.longitude}
              onChange={event => handleChange(event.target.name, event.target.value)}
              disabled={readOnlyLocal}
              format="-##,#######"
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 4 }}>
            <InputV2
              error={!!(errors && errors.situacaoPoste)}
              isRequired
              label="Situação do Poste"
              name="situacaoPoste"
              onChange={event => handleChange(event.target.name, event.target.value)}
              value={values.situacaoPoste}
              disabled={readOnlyLocal}
            />
          </Grid>
          {(values.situacaoPoste === 'Não compartilhável' || values.situacaoPoste === 'Sem irregularidade') && (
            <Grid size={{ xs: 12 }} sx={fieldContainerSx}>
              <InputV2
                error={!!(errors && errors.observacao)}
                isRequired={values.situacaoPoste === 'Não compartilhável' || values.situacaoPoste === 'Sem irregularidade'}
                label="Descrição/Observação"
                name="observacao"
                onChange={event => handleChange(event.target.name, event.target.value)}
                placeholder="observacao"
                value={values.observacao}
                rows={2}
                multiline
                disabled={readOnlyLocal}
              />
            </Grid>
          )}
          <Grid size={{ xs: 12 }} sx={rootGridSx}>
            {renderOcorrencias()}
          </Grid>
          <ArquivoContainerV2
            collapse
            arquivos={values.fotos}
            showDescription
            onSave={fotos => {
              setEditFiles(true);
              setValues({ ...values, fotos });
            }}
            disabled={canEditNsmp ? false : readOnlyLocal}
          />
          <Grid size={{ xs: 12 }}>
            {errors && errors.fotos && (
              <DialogContentText sx={requiredTextSx}>*Imagens são obrigatórias</DialogContentText>
            )}
          </Grid>
        </Grid>
      </DialogDefaultV2>
      <DialogDefaultV2
        open={ocorrenciaData.open}
        handlerCancel={() => setOcorrenciaData({ open: false, type: '', subNmspId: null, subNsmpCodigo: null })}
        handlerConfirm={() => {
          validateInvalidateSubNsmp(ocorrenciaData.subNmspId, ocorrenciaData.type, () => {
            const updateSubNsmps = [];
            values.subNsmps.forEach(subNsmp => {
              if (subNsmp.id === ocorrenciaData.subNmspId) {
                subNsmp.validate = ocorrenciaData.type;
              }
              updateSubNsmps.push(subNsmp);
            });

            setValues({ ...values, subNsmps: updateSubNsmps });
            setOcorrenciaData({ open: false, type: '', subNmspId: null, subNsmpCodigo: null });
          });
        }}
        title={`Confirmação - ${ocorrenciaData.type === 'INVALIDADO' ? 'INVALIDAR' : 'VALIDAR'}`}
        confirmMessage="Sim"
      >
        <DialogContentText>
          {ocorrenciaData.type === 'VALIDADO'
            ? `Deseja validar a ocorrência ${ocorrenciaData.subNsmpCodigo}?`
            : `Deseja invalidar a ocorrência ${ocorrenciaData.subNsmpCodigo}?`}
        </DialogContentText>
      </DialogDefaultV2>
      <DialogDefaultV2
        open={clonarData.open}
        handlerCancel={() => setClonarData({ open: false, subNmspId: null, subNsmpCodigo: null })}
        handlerConfirm={() => {
          cloneSubNsmp(clonarData.subNmspId, subNsmpClone => {
            setValues({ ...values, subNsmps: sortByCode([...values.subNsmps, subNsmpClone]) });
            setClonarData({ open: false, subNmspId: null, subNsmpCodigo: null });
          });
        }}
        title="Confirmação - Clonagem"
        confirmMessage="Sim"
      >
        <DialogContentText>
          {`Deseja clonar a ocorrência ${clonarData.subNsmpCodigo}?`}
        </DialogContentText>
      </DialogDefaultV2>
      <DialogDefaultV2
        open={deletarData.open}
        handlerCancel={() => setDeletarData({ open: false, subNmspId: null, subNsmpCodigo: null })}
        handlerConfirm={() => {
          deleteCloneSubNsmp(deletarData.subNmspId, subNsmpUpdatedList => {
            const removerIds = new Set(subNsmpUpdatedList.map(d => d.id));
            removerIds.add(deletarData.subNmspId);

            const resultado = values.subNsmps.filter(d => !removerIds.has(d.id));

            setValues({ ...values, subNsmps: sortByCode([...resultado, ...subNsmpUpdatedList]) });
            setDeletarData({ open: false, subNmspId: null, subNsmpCodigo: null });
          });
        }}
        title="Confirmação - Deletar"
        confirmMessage="Sim"
      >
        <DialogContentText>
          {`Deseja deletar a ocorrência ${deletarData.subNsmpCodigo}?`}
        </DialogContentText>
      </DialogDefaultV2>
    </>
  );
};

const mapStateToProps = state => ({
  nsmps: selectorsNsmp.getNsmps(state),
  nsmpSelected: selectorsNsmp.getNsmpSelected(state),
  userRole: selectorsAuth.getRole(state),
  authData: selectorsAuth.getAuth(state),
  nsirStatus: selectorsNsir.getNsirStatus(state),
  userId: selectorsAuth.getUserId(state),
});

const mapDispatchToProps = dispatch => bindActionCreators({
  ...actionsNsmp,
  ...actionsSubNsmp,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(UsoMutuoNewModal);
